import type {
  Filter,
  FilterDataMultiSelect,
  FilterDataOption,
  FilterDataSearch,
  FilterDataSearchDynamic,
  FilterSectionItem,
} from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { urlParamOf } from '@/app/common/filters/core/filterDefinitions.ts';
import type { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { OPTIONS_SORT, sortOptions } from '@/app/common/filters/types/shared/utils/sort.ts';
import { counterOf, disabledAtZero } from '@/app/common/filters/groups/optionCounts.ts';

type SimpleOptionCreateProps = {
  labelKey: string;
  value: string;
};

type SimpleOptionCreatorProps = {
  selectedValues: string[];
  translationDictionary: TranslationDictionary;
};

export type SimpleOptionCreate = (props: SimpleOptionCreateProps) => FilterDataOption;
export type SimpleOptionCreator = (props: SimpleOptionCreatorProps) => SimpleOptionCreate;

export const simpleOptionCreator =
  ({ selectedValues, translationDictionary }: SimpleOptionCreatorProps) =>
  ({ labelKey, value }: SimpleOptionCreateProps) => ({
    label: getTranslationServer(labelKey, translationDictionary),
    selected: selectedValues.includes(value),
    value,
  });

type DynamicDataSimple = { [key: string]: number | undefined };

type DynamicDataWithLabelItem = {
  value: string;
  label: string;
  addLabel: string;
};

type CreateDynamicWithLabelOptions = {
  selectedValue: string | undefined;
  dynamicData: DynamicDataWithLabelItem[];
  anyLabel?: string;
};

const ANY_VALUE = '0';

export const createDynamicWithLabelOptions = ({
  selectedValue,
  dynamicData,
  anyLabel,
}: CreateDynamicWithLabelOptions): FilterDataOption[] => {
  const selectedValues = selectedValue ? selectedValue.split(',') : [];
  return dynamicData
    .map((item) => (!!anyLabel && item.value === ANY_VALUE ? { ...item, label: anyLabel } : item))
    .map(
      (item) =>
        ({
          label: item.label,
          selected: selectedValues.includes(item.value),
          value: item.value,
          counter: Number.isNaN(Number(item.addLabel)) ? 0 : Number(item.addLabel),
        }) as FilterDataOption
    );
};

type CreateSearchFilterWithLabel = {
  filterName: FILTER_NAME;
  searchParams: SearchParams;
  urlParameterName: string;
  dynamicData: DynamicDataWithLabelItem[] | undefined;
  placeholder: string;
  anyLabel?: string;
  noResultsLabel?: string;
  optionsSort?: OPTIONS_SORT;
};

export const createSearchFilterWithLabel = ({
  filterName,
  searchParams,
  urlParameterName,
  dynamicData,
  placeholder,
  anyLabel,
  noResultsLabel,
  optionsSort = OPTIONS_SORT.NONE,
}: CreateSearchFilterWithLabel): Filter[] => {
  const showFilter = Array.isArray(dynamicData) && dynamicData.length > 0;
  const selectedValue = searchParams[urlParameterName];

  const searchFilter: Filter | undefined = showFilter
    ? {
        name: filterName,
        type: FILTER_TYPE.SEARCH,
        data: {
          placeholder,
          noResultsLabel,
          options: sortOptions(optionsSort, createDynamicWithLabelOptions({ selectedValue, dynamicData, anyLabel })),
          ...(anyLabel && { anyValue: ANY_VALUE }),
        } as FilterDataSearch,
      }
    : undefined;

  return [...[searchFilter]].filter((f) => !!f);
};

type CreateSearchDynamicFilter = {
  filterName: FILTER_NAME;
  searchParams: SearchParams;
  urlParameterName: string;
  label: string;
  placeholder: string;
  noResultsLabel?: string;
};

export const createSearchDynamicFilter = ({
  filterName,
  searchParams,
  urlParameterName,
  label,
  placeholder,
  noResultsLabel,
}: CreateSearchDynamicFilter): Filter[] => {
  const selectedValue = searchParams[urlParameterName] || '';

  const searchFilter: Filter = {
    name: filterName,
    type: FILTER_TYPE.SEARCH_DYNAMIC,
    data: {
      label,
      placeholder,
      noResultsLabel,
      value: selectedValue,
    } as FilterDataSearchDynamic,
  };

  return [...[searchFilter]].filter((f) => !!f);
};

type CreateMultiSelectFilterSubsetOptions = {
  filterSectionItems: FilterSectionItem[];
  dynamicFilter: DynamicDataSimple | undefined;
  selectedValues: string[];
  translationDictionary: TranslationDictionary;
  optionsSort?: OPTIONS_SORT;
};

const createMultiSelectFilterSubsetOptions = ({
  filterSectionItems,
  dynamicFilter,
  selectedValues,
  translationDictionary,
  optionsSort = OPTIONS_SORT.AUTO,
}: CreateMultiSelectFilterSubsetOptions): FilterDataOption[] => {
  // A selected value is never dropped: without its option there is no badge and no reset button.
  const options = filterSectionItems
    .filter(
      (filterSectionItem) =>
        dynamicFilter?.[filterSectionItem.id] !== undefined || selectedValues.includes(`${filterSectionItem.id}`)
    )
    .map((filterSectionItem) => {
      const selected = selectedValues.includes(`${filterSectionItem.id}`);
      const counter = counterOf(dynamicFilter, filterSectionItem.id);
      return {
        label: getTranslationServer(filterSectionItem.translationKey, translationDictionary),
        selected,
        disabled: disabledAtZero(selected, counter),
        value: `${filterSectionItem.id}`,
        counter,
      } as FilterDataOption;
    });
  return sortOptions(optionsSort, options);
};

type GetSelectedValuesProps = {
  name: string;
  searchParams: SearchParams;
};

export const getSelectedValues = ({ name, searchParams }: GetSelectedValuesProps): string[] => {
  const parameter = urlParamOf(name);
  const urlValue = parameter ? searchParams[parameter] : undefined;
  return urlValue ? urlValue.split(',') : [];
};

type CreateMultiSelectFilterSubset = {
  name: FILTER_NAME;
  filterSectionItems: FilterSectionItem[];
  searchParams: SearchParams;
  dynamicFilter: DynamicDataSimple | undefined;
  labelKey: string;
  translationDictionary: TranslationDictionary;
  optionsSort?: OPTIONS_SORT;
};
export const createMultiSelectFilterSubset = ({
  name,
  filterSectionItems,
  searchParams,
  dynamicFilter,
  labelKey,
  translationDictionary,
  optionsSort = OPTIONS_SORT.AUTO,
}: CreateMultiSelectFilterSubset): Filter | undefined => {
  const selectedValues = getSelectedValues({ name, searchParams });
  const options = createMultiSelectFilterSubsetOptions({
    filterSectionItems,
    dynamicFilter,
    selectedValues,
    translationDictionary,
    optionsSort,
  });

  return {
    name,
    type: FILTER_TYPE.MULTI_SELECT,
    data: {
      label: getTranslationServer(labelKey, translationDictionary),
      options,
    } satisfies FilterDataMultiSelect,
  };
};
